/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
/* eslint-disable max-len */

import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { animated } from '@react-spring/web';
import { ChevronDown } from 'lucide-react';
import { ButtonHTMLAttributes, FC, MouseEvent, PropsWithChildren, ReactNode, RefObject, useCallback } from 'react';
import { CSSProperties } from 'styled-components';
import useBoop from 'use-boop';

import { themes } from './Button.themes';

import { useHoverEffect } from '@/pages/projects/HoverEffect/HoverEffect';
import { cn } from '@/utils';

interface DropdownItem {
    label: string;
    icon?: ReactNode;
    onClick: () => void;
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement & HTMLAnchorElement> {
    theme?: 'default' | 'hollow';
    size: 'small' | 'large';
    color: 'white' | 'purple' | 'slate';
    asLink?: boolean;
    dropdown?: DropdownItem[];
}

const ButtonContent = ({
    children,
    dropdown,
    spacing,
    disabled,
    boopTrigger,
    onClick,
}: {
    children: ReactNode;
    dropdown?: DropdownItem[];
    spacing: string;
    disabled?: boolean;
    boopTrigger: () => void;
    onClick?: (e: any) => void;
}) => {
    const handleClick = useCallback(
        (e: MouseEvent<HTMLDivElement>) => {
            boopTrigger();
            onClick?.(e);
        },
        [onClick, boopTrigger],
    );
    return (
        <div
            onClick={(e) => {
                e.stopPropagation();
                if (!disabled) {
                    handleClick(e);
                }
            }}
            className={cn('flex-1 flex items-center relative', spacing)}
        >
            {children}
            {dropdown && (
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[1px] h-[60%]">
                    <div className="w-full h-full bg-current opacity-[0.08] shadow-[1px_0_0_rgba(255,255,255,0.1)] rounded" />
                </div>
            )}
        </div>
    );
};

const DropdownTrigger = () => (
    <div className={cn('flex items-center h-full px-1')}>
        <ChevronDown size={16} />
    </div>
);

const DropdownContent = ({ items }: { items: DropdownItem[] }) => (
    <DropdownMenu.Portal>
        <DropdownMenu.Content
            className="z-50 min-w-[8rem] max-w-[min(calc(100vw,360px)] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
            align="end"
        >
            {items.map((item, index) => (
                <DropdownMenu.Item
                    key={index}
                    className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                    onClick={(e) => {
                        e.stopPropagation();
                        item.onClick();
                    }}
                >
                    {item.icon && <span className="mr-2">{item.icon}</span>}
                    {item.label}
                </DropdownMenu.Item>
            ))}
        </DropdownMenu.Content>
    </DropdownMenu.Portal>
);

export const Button: FC<PropsWithChildren<ButtonProps>> = ({
    children,
    theme = 'default',
    color = 'purple',
    size = 'small',
    asLink = false,
    dropdown,
    onClick,
    ...props
}) => {
    const [boopStyle, boopTrigger] = useBoop({
        scale: 0.95,
        springConfig: { tension: 280, friction: 20 },
    });

    const { spacing, themeClass } = themes(theme, color, size);
    const finalClassName = cn(
        'pointer-events-auto rounded-md cursor-pointer font-medium text-body',
        themeClass,
        props.className,
        'disabled:pointer-events-none disabled:opacity-50',
    );

    const hoverRef = useHoverEffect({
        scale: 1.02,
        duration: 0.1,
        shadow: true,
        shadowColor: '000',
        shadowOpacity: 0.1,
        modifier: 3,
    });

    const Element = asLink ? animated.a : animated.button;

    return (
        <Element
            {...props}
            style={boopStyle as CSSProperties}
            className={cn(finalClassName, 'flex items-center')}
            ref={hoverRef as unknown as RefObject<HTMLButtonElement> & RefObject<HTMLAnchorElement>}
        >
            <ButtonContent
                dropdown={dropdown}
                spacing={spacing}
                disabled={props.disabled}
                boopTrigger={boopTrigger}
                onClick={onClick}
            >
                {children}
            </ButtonContent>

            {dropdown && (
                <DropdownMenu.Root>
                    <DropdownMenu.Trigger className="self-stretch">
                        <DropdownTrigger />
                    </DropdownMenu.Trigger>
                    <DropdownContent items={dropdown} />
                </DropdownMenu.Root>
            )}
        </Element>
    );
};
